import { DiscoverLink } from '../pages/Discover';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ThemeToggle } from '../contexts/ThemeContext';
import { Avatar } from './Avatar';
import { isApprovedAdmin } from '../data/approvedAdmins';
import { useActiveProfile, getActiveProfileId } from '../utils/profile';
import { POINT_ACHIEVEMENTS, GENRE_ACHIEVEMENTS } from '../data/achievements';

// Flat lookup so notification rendering can show "Unlocked: First Step"
// instead of the raw id. Special-case achievements that don't live in
// the two catalogues (first-favourite / bilingual-reader / streak-3)
// fall back to a sensible default in the renderer.
const ACHIEVEMENT_BY_ID = (() => {
  const map = {};
  for (const a of POINT_ACHIEVEMENTS) map[a.id] = a;
  for (const list of Object.values(GENRE_ACHIEVEMENTS)) {
    for (const a of list) map[a.id] = a;
  }
  map['first-favourite']   = { id: 'first-favourite',   label: 'First Favourite',   icon: '⭐' };
  map['bilingual-reader']  = { id: 'bilingual-reader',  label: 'Bilingual Reader',  icon: '🌐' };
  map['streak-3']          = { id: 'streak-3',          label: '3-Pass Streak',     icon: '🔥' };
  return map;
})();

function relativeTime(iso) {
  if (!iso) return '';
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return '';
  const secs = Math.max(1, Math.floor((Date.now() - d) / 1000));
  if (secs < 60)   return `${secs}s ago`;
  if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
  if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
  return `${Math.floor(secs / 86400)}d ago`;
}

function readUser() {
  try {
    const raw = typeof window !== 'undefined' ? localStorage.getItem('adeeb-user') : null;
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

// One-shot refresh of the cached user from /me so newly-granted roles
// (admin) are reflected without forcing a re-login.
async function refreshUserCache() {
  if (typeof window === 'undefined') return;
  const token = localStorage.getItem('adeeb-token');
  if (!token) return;
  try {
    const { data } = await axios.get('/api/auth/me', {
      headers: { Authorization: `Bearer ${token}` },
      timeout: 6000,
    });
    if (data?.user && localStorage.getItem('adeeb-token') === token) {
      const cached = readUser() || {};
      // Only rewrite if something actually differs (avoids needless event spam)
      const next = { ...cached, ...data.user };
      if (JSON.stringify(next) !== JSON.stringify(cached)) {
        localStorage.setItem('adeeb-user', JSON.stringify(next));
        window.dispatchEvent(new Event('adeeb-auth-change'));
      }
    }
  } catch { /* ignore */ }
}

/**
 * Navbar item for the School portal — clicking it opens a small menu
 * with the two related destinations (the staff/student portal and the
 * public "join as a school" page). Click outside or pick an item to
 * close. Click the label itself navigates straight to /school-portal.
 */
function SchoolPortalMenu() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  // Re-read the user from localStorage on every render so the menu
  // adapts the moment a staff user signs in or out.
  let cachedUser = null;
  try { cachedUser = JSON.parse(localStorage.getItem('adeeb-user') || 'null'); }
  catch { /* ignore */ }
  const isStaff = cachedUser?.accountType === 'teacher' || cachedUser?.accountType === 'school';
  // Close on outside click + on route change.
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [open]);
  useEffect(() => { setOpen(false); }, [location.pathname]);

  const isOnSchoolRoute = /^\/(school-portal|school-login|school-join|teacher)/.test(location.pathname);

  return (
    <div className="site-nav-school" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className={`site-nav-link site-nav-link-admin site-nav-school-toggle${isOnSchoolRoute ? ' is-active' : ''}${open ? ' is-open' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        School portal
        <span className="site-nav-school-caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="site-nav-school-menu" role="menu">
          {isStaff && (
            <Link to="/teacher" className="site-nav-school-item" onClick={() => setOpen(false)}>
              <span className="site-nav-school-item-name">Teacher panel</span>
              <span className="site-nav-school-item-sub">Manage classes &amp; students</span>
            </Link>
          )}
          <Link to="/school-portal" className="site-nav-school-item" onClick={() => setOpen(false)}>
            <span className="site-nav-school-item-name">Sign in</span>
            <span className="site-nav-school-item-sub">Student or staff</span>
          </Link>
          <Link to="/school-join" className="site-nav-school-item" onClick={() => setOpen(false)}>
            <span className="site-nav-school-item-name">Join DeenLit</span>
            <span className="site-nav-school-item-sub">Pricing &amp; apply</span>
          </Link>
        </div>
      )}
    </div>
  );
}

function NavAuth({ user, logout }) {
  const navigate = useNavigate();
  const location = useLocation();
  const profile = useActiveProfile();
  // Two independent popups: clicking the avatar opens the profile menu;
  // clicking the badge opens the notifications popup. Only one is open
  // at a time, so opening either closes the other.
  const [profileOpen, setProfileOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Admin accounts get their own User-scoped feed (school-pending,
  // contact-message). Non-admins still need an active reader profile
  // for the original per-profile feed (book-assigned, achievement-unlocked).
  const isAdmin = user?.role === 'admin' && location.pathname.startsWith('/admin');

  const refetchNotifs = useCallback(async () => {
    const pid = profile?._id;
    if (!isAdmin && !pid) { setNotifications([]); setUnreadCount(0); return; }
    try {
      const r = await axios.get('/api/notifications', { params: isAdmin ? { scope: 'admin' } : {} });
      if (!isAdmin && getActiveProfileId() !== pid) return;
      setNotifications(Array.isArray(r?.data?.items) ? r.data.items : []);
      setUnreadCount(Number.isFinite(r?.data?.unread) ? r.data.unread : 0);
    } catch { /* ignore — request can 400 when no profile and not admin */ }
  }, [profile?._id, isAdmin]);

  useEffect(() => {
    let alive = true;
    setNotifications([]); setUnreadCount(0); setNotifOpen(false);
    (async () => {
      const pid = profile?._id;
      if (!isAdmin && !pid) { setNotifications([]); setUnreadCount(0); return; }
      try {
        const r = await axios.get('/api/notifications', { params: isAdmin ? { scope: 'admin' } : {} });
        if (!alive) return;
        setNotifications(Array.isArray(r?.data?.items) ? r.data.items : []);
        setUnreadCount(Number.isFinite(r?.data?.unread) ? r.data.unread : 0);
      } catch { /* ignore */ }
    })();
    return () => { alive = false; };
  }, [profile?._id, location.pathname, isAdmin]);

  // Stay in sync with the other notification surface (the /profile
  // page badge). Either side dispatches `adeeb-notifications-change`
  // after marking notifications read; both refetch on the event.
  useEffect(() => {
    const h = () => refetchNotifs();
    window.addEventListener('adeeb-notifications-change', h);
    window.addEventListener('adeeb-discover-change', h);
    window.addEventListener('focus', h);
    const timer = setInterval(h, 15000);
    return () => { window.removeEventListener('adeeb-notifications-change', h); window.removeEventListener('adeeb-discover-change', h); window.removeEventListener('focus', h); clearInterval(timer); };
  }, [refetchNotifs]);

  // Either popup closes on a click outside the profile/badge container.
  useEffect(() => {
    if (!profileOpen && !notifOpen) return;
    const close = () => { setProfileOpen(false); setNotifOpen(false); };
    window.addEventListener('click', close);
    return () => window.removeEventListener('click', close);
  }, [profileOpen, notifOpen]);

  const markRead = useCallback(async (id) => {
    const pid = getActiveProfileId();
    try {
      await axios.post(`/api/notifications/${id}/read`, null, { params: isAdmin ? { scope: 'admin' } : {} });
      if (!isAdmin && getActiveProfileId() !== pid) return;
      setNotifications((list) => list.map((n) => n._id === id ? { ...n, read: true } : n));
      setUnreadCount((c) => Math.max(0, c - 1));
      window.dispatchEvent(new Event('adeeb-notifications-change'));
    } catch { /* ignore */ }
  }, [isAdmin]);

  const markAllRead = useCallback(async () => {
    const pid = getActiveProfileId();
    try {
      await axios.post('/api/notifications/mark-all-read', null, { params: isAdmin ? { scope: 'admin' } : {} });
      if (!isAdmin && getActiveProfileId() !== pid) return;
      setNotifications((list) => list.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
      window.dispatchEvent(new Event('adeeb-notifications-change'));
    } catch { /* ignore */ }
  }, [isAdmin]);

  const onNotificationClick = useCallback((n) => {
    if (!n.read) markRead(n._id);
    setNotifOpen(false);
    if (n.type === 'discover-available') { navigate('/discover'); return; }
    if (n.type === 'book-assigned' && n.bookId) {
      navigate(`/read/${n.bookId}`);
    } else if (n.type === 'achievement-unlocked') {
      navigate('/profile');
    } else if (n.type === 'school-pending') {
      navigate('/admin/schools');
    } else if (n.type === 'contact-message') {
      navigate('/admin/messages');
    }
  }, [markRead, navigate]);

  const renderedNotifications = useMemo(() => notifications.slice(0, 8), [notifications]);

  // Avatar in the navbar reflects the *active reading profile*, not the
  // parent account. Falls back to the parent's avatar when no profile is
  // selected (e.g. fresh login, mid-switch).
  const avatarValue = profile?.avatar || user.avatar;
  const displayName = profile?.name || user.name || user.email;
  const greetingFirst = (profile?.name || user.name || 'reader').split(' ')[0];

  // Staff (teachers / school admins) and student accounts don't have the
  // multi-profile + parent-billing system. Hide "Switch profile" and
  // "Account settings" for them; those screens are parent-account only.
  const isStaff = user.accountType === 'teacher' || user.accountType === 'school' || user.accountType === 'student';

  const onSwitch = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setProfileOpen(false);
    navigate('/profiles/select');
  };

  return (
    <div className="site-nav-auth">
      <span className="site-nav-greeting">Hi, {greetingFirst}</span>
      <div className="site-nav-profile" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="site-nav-avatar"
          aria-label={`Active profile: ${displayName}`}
          aria-haspopup="menu"
          aria-expanded={profileOpen}
          title={displayName}
          onClick={(e) => { e.stopPropagation(); setProfileOpen((v) => !v); setNotifOpen(false); }}
        >
          <Avatar value={avatarValue} name={displayName} size={36} />
        </button>
        <button
          type="button"
          className={`site-nav-notif-badge-btn${unreadCount > 0 ? '' : ' is-zero'}`}
          aria-label={unreadCount > 0 ? `${unreadCount} unread notifications` : 'Notifications (no new messages)'}
          aria-haspopup="menu"
          aria-expanded={notifOpen}
          onClick={(e) => { e.stopPropagation(); setNotifOpen((v) => !v); setProfileOpen(false); }}
        >
          {unreadCount > 9 ? '9+' : unreadCount}
        </button>
        {profileOpen && (
          <div className="site-nav-profile-menu" role="menu">
            <div className="site-nav-profile-head">
              <Avatar value={avatarValue} name={displayName} size={48} />
              <div>
                <div className="site-nav-profile-name">{displayName}</div>
                <div className="site-nav-profile-sub">
                  {profile ? 'Active profile' : 'No profile selected'}
                </div>
              </div>
            </div>
            <Link to="/profile" className="site-nav-profile-item" onClick={() => setProfileOpen(false)}>
              View my profile
            </Link>
            {!isStaff && (
              <button type="button" className="site-nav-profile-item" onClick={onSwitch}>
                Switch profile
              </button>
            )}
            {!isStaff && (
              <Link to="/settings" className="site-nav-profile-item" onClick={() => setProfileOpen(false)}>
                Account settings
              </Link>
            )}
          </div>
        )}
        {notifOpen && (
          <div className="site-nav-notif-popup" role="menu" onClick={(e) => e.stopPropagation()}>
            <div className="site-nav-notif-popup-head">
              <span className="site-nav-notif-popup-title">Notifications</span>
              {unreadCount > 0 && (
                <button type="button" className="site-nav-notif-popup-clear" onClick={markAllRead}>
                  Mark all read
                </button>
              )}
            </div>
            {renderedNotifications.length === 0 ? (
              <div className="site-nav-notif-popup-empty">You're all caught up.</div>
            ) : (
              <div className="site-nav-notif-popup-list">
                {unreadCount === 0 && (
                  <div className="site-nav-notif-popup-caughtup">
                    You're all caught up. Older notifications below.
                  </div>
                )}
                {renderedNotifications.map((n) => {
                  const ach = n.achievementId ? ACHIEVEMENT_BY_ID[n.achievementId] : null;
                  const title = n.type === 'achievement-unlocked' && ach
                    ? `Unlocked: ${ach.label}`
                    : n.message || 'You have a new notification.';
                  const icon =
                    n.type === 'achievement-unlocked' ? (ach?.icon || '🏆') :
                    n.type === 'school-pending'       ? '🏫' :
                    n.type === 'contact-message'      ? '✉️' :
                                                        '📘';
                  return (
                    <button
                      key={n._id}
                      type="button"
                      className={`site-nav-notif-item${n.read ? '' : ' is-unread'}`}
                      onClick={() => onNotificationClick(n)}
                    >
                      <span className="site-nav-notif-icon" aria-hidden="true">{icon}</span>
                      <span className="site-nav-notif-body">
                        <span className="site-nav-notif-title">{title}</span>
                        <span className="site-nav-notif-time">{relativeTime(n.createdAt)}</span>
                      </span>
                      {!n.read && <span className="site-nav-notif-dot" aria-hidden="true" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
      <button type="button" className="auth-button auth-button-logout" onClick={logout}>
        Logout
      </button>
    </div>
  );
}

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [user, setUser] = useState(readUser);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const refresh = () => setUser(readUser());
    window.addEventListener('storage', refresh);
    window.addEventListener('adeeb-auth-change', refresh);
    // Pull the latest profile in the background so role grants/revokes
    // take effect without forcing the user to sign out and back in.
    refreshUserCache();
    return () => {
      window.removeEventListener('storage', refresh);
      window.removeEventListener('adeeb-auth-change', refresh);
    };
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  if (location.pathname.startsWith('/read/')) return null;

  const logout = async () => {
    try { const r = await fetch('/api/auth/logout', { method: 'POST', credentials: 'same-origin' }); if (!r.ok && r.status !== 401) return; } catch { return; }
    localStorage.removeItem('adeeb-token');
    localStorage.removeItem('adeeb-user');
    localStorage.removeItem('adeeb-session-mode');
    window.dispatchEvent(new Event('adeeb-auth-change'));
    setUser(null);
    navigate('/');
  };

  const navLinks = [
    { to: '/', label: 'Home', end: true },
    { to: '/category/all', label: 'Library' },
  ];

  return (
    <header className={`site-nav ${open ? 'is-open' : ''}`}>
      <div className="site-nav-inner">
        <Link to="/" className="site-nav-brand" aria-label="Deenlit home">
          <img className="site-nav-logo" src="/branding/deenlit-logo.png" alt="DeenLit" width="2172" height="724" />
          <span className="site-nav-brand-sub">A World of Stories</span>
        </Link>

        <button
          type="button"
          className="site-nav-toggle"
          aria-label="Toggle menu"
          aria-expanded={open}
          onClick={() => setOpen((o) => !o)}
        >
          <span /><span /><span />
        </button>

        <nav className="site-nav-links" aria-label="Primary">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.end}
              className={({ isActive }) =>
                `site-nav-link${isActive ? ' is-active' : ''}`
              }
            >
              {link.label}
            </NavLink>
          ))}
          <DiscoverLink />
          {user && (
            <NavLink
              to="/profile"
              className={({ isActive }) =>
                `site-nav-link${isActive ? ' is-active' : ''}`
              }
            >
              My Shelf
            </NavLink>
          )}
          <NavLink
            to="/about"
            className={({ isActive }) =>
              `site-nav-link${isActive ? ' is-active' : ''}`
            }
          >
            About
          </NavLink>
          <SchoolPortalMenu />
          {isApprovedAdmin(user) && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                `site-nav-link site-nav-link-admin${isActive ? ' is-active' : ''}`
              }
            >
              Admin
            </NavLink>
          )}
        </nav>

        <div className="site-nav-actions">
          <ThemeToggle className="theme-toggle--inline" />
          <span className="page-tour-host" />
          {user ? (
            <NavAuth user={user} logout={logout} />
          ) : (
            <div className="site-nav-auth">
              <Link to="/login" className="auth-button">Login</Link>
              <Link to="/register" className="auth-button auth-button-primary">Sign up</Link>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
